import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, map, Observable, of, retry, switchMap, throwError, timer } from 'rxjs';
import { ApiErrorDetails } from '../../shared/models/errors/api-error-details';
import { AppInsightsService } from '../services/app-insights.service';
import { HotToastService } from '@ngxpert/hot-toast';

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// Requests with responseType 'blob' deliver the error body as Blob, malformed JSON bodies as raw string.
function readErrorBody(error: HttpErrorResponse): Observable<ApiErrorDetails | null> {
  if (error.error instanceof Blob) {
    return from(error.error.text()).pipe(
      map((text) => parseJson(text) as ApiErrorDetails | null),
      catchError(() => of(null))
    );
  }
  if (typeof error.error === 'string') {
    return of(parseJson(error.error) as ApiErrorDetails | null);
  }
  return of(error.error);
}

export const apiErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(HotToastService);
  const appInsightsService = inject(AppInsightsService);

  const abortController = new AbortController();
  const abortSignal = abortController.signal;

  document.addEventListener('userAction', () => {
    abortController.abort();
  });

  return next(req).pipe(
    retry({
      count: 10,
      delay: (error, retryCount) => {
        if (abortSignal.aborted) {
          return throwError(() => error);
        }
        if (error instanceof HttpErrorResponse && error.status === 0 && retryCount < 10) {
          const delayTime = Math.min(Math.pow(2, retryCount) * 1000, 16000);
          toast.info('🍵 Lost server connection, trying to reconnect', { id: 'api_error_reconnect' });
          return timer(delayTime);
        }
        return throwError(() => error);
      },
    }),
    catchError((error: HttpErrorResponse) => {
      appInsightsService.logException(error, 'Processing API call');

      if (error.status === 0) {
        toast.error('Server is not available', { id: 'api_error_0' });
      } else if (error.status === 409) {
        toast.warning(
          'Conflict detected: The entity has been modified by another user. Review the changes by opening the same entity in a different browser tab. Refresh this page to discard your changes.',
          { duration: 10000, id: 'api_error_409' }
        );
      } else {
        return readErrorBody(error).pipe(
          switchMap((details) => {
            if (details) {
              const rawMsg = details.message ?? '';
              const formattedMsg = rawMsg
                .split(';')
                .map((p) => p.trim())
                .filter((p) => p.length > 0)
                .join('<br/><br/>');

              toast.warning(`Status: ${error.status}` + '<br/><br/>' + formattedMsg, {
                duration: 5000,
                id: 'api_error_other_mulitline',
              });
            } else {
              toast.error(`Status: ${error.status}<br/><br/>${error.message}`, { id: 'api_error_other' });
            }
            return throwError(() => error);
          })
        );
      }
      return throwError(() => error);
    })
  );
};
